import {
  Button,
  Icons,
  LoadingIndicator,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@commenda/kakashi';
import { formatRelativeTime } from '@commenda/utils';
import { useState } from 'react';

import type { TWorkflowActivationReadState } from './workflow-version-activation-helper';

export type TPublishWorkflowPopoverProps = {
  /** Shown as the subtitle, e.g. "Version 1.0.0". */
  versionLabel: string;
  /** When the version was last saved. Omitted drops the clause rather than saying "never". */
  lastSavedAt?: string;
  isPublished: boolean;
  isActive: boolean;
  isPublishing: boolean;
  isActivating: boolean;
  /** Resolves true when the publish succeeded, which is what advances the popover to step two. */
  onPublish: () => Promise<boolean>;
  onActivate: () => Promise<void> | void;
  /** Absent hides the row: an app with no link flow should not offer one. */
  onCreateLink?: () => void;
  /** What the author should know before going live, listed on step two above Make live. */
  activationWarnings?: readonly string[];
  /** The live state and the warnings come from a template read; Make live waits until it lands. */
  activationReadState?: TWorkflowActivationReadState;
};

/**
 * Publishing and going live, in the order they actually depend on each other.
 *
 * This replaced a toggle, which read as a setting of the publish and was really a separate action:
 * a version cannot be made active before it exists, so pre-committing the choice asked the author to
 * decide something about a thing that was not there yet. The popover advances instead. It publishes,
 * then asks about going live, and each step is one button.
 *
 * Reopening on a version that is already published starts at step two, and on one that is already
 * live says so and offers nothing.
 */
export const PublishWorkflowPopover = ({
  versionLabel,
  lastSavedAt,
  isPublished,
  isActive,
  isPublishing,
  isActivating,
  onPublish,
  onActivate,
  onCreateLink,
  activationWarnings = [],
  activationReadState = 'ready',
}: TPublishWorkflowPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // The version prop refreshes a moment after publishing returns, so the popover would flick back to
  // step one while that lands. This holds step two from the instant the publish succeeds.
  const [hasJustPublished, setHasJustPublished] = useState(false);

  const isOnStepTwo = isPublished || hasJustPublished;
  const isActivationKnown = activationReadState === 'ready';
  const isShownLive = isActive && isActivationKnown;
  const subtitle = isOnStepTwo
    ? `${versionLabel} · Published`
    : lastSavedAt
      ? `${versionLabel} · Last saved ${formatRelativeTime(lastSavedAt)}`
      : versionLabel;

  const close = () => {
    setIsOpen(false);
    setHasJustPublished(false);
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(next) => (next ? setIsOpen(true) : close())}
    >
      <PopoverTrigger asChild>
        <Button size="sm" rightIcon={<Icons.ChevronDown className="size-4" />}>
          Publish
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-[340px] flex-col p-none">
        <div className="flex flex-col gap-xxs p-lg">
          <span className="txt-small-bold text-text-primary">
            Publish workflow
          </span>
          <span className="flex items-center gap-xs txt-extra-small-regular text-text-tertiary">
            {isOnStepTwo && (
              <Icons.CheckCircle className="size-3 shrink-0 text-text-success" />
            )}
            {subtitle}
          </span>
        </div>

        <div className="flex flex-col gap-xxs border-t border-border-secondary p-lg">
          {isOnStepTwo && activationReadState === 'loading' ? (
            <span className="flex items-center gap-md txt-extra-small-regular text-text-tertiary">
              <LoadingIndicator size={16} />
              Checking which version is live…
            </span>
          ) : isOnStepTwo && activationReadState === 'error' ? (
            <span className="txt-extra-small-regular text-text-error">
              Couldn&apos;t check which version is live. Refresh to try again.
            </span>
          ) : isShownLive ? (
            <>
              <span className="txt-small-medium text-text-primary">
                This version is live
              </span>
              <span className="txt-extra-small-regular text-text-tertiary">
                New runs are already using it.
              </span>
            </>
          ) : isOnStepTwo ? (
            <>
              <span className="txt-small-medium text-text-primary">
                Step 2 of 2 · Make it live
              </span>
              <span className="txt-extra-small-regular text-text-tertiary">
                New runs will use this version. The version that is live today
                keeps running until you do.
              </span>
              {activationWarnings.length > 0 && (
                <ul className="flex list-disc flex-col gap-xxs pl-lg txt-extra-small-regular text-text-secondary">
                  {activationWarnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              <span className="txt-small-medium text-text-primary">
                Step 1 of 2 · Publish this version
              </span>
              <span className="txt-extra-small-regular text-text-tertiary">
                Validation runs on publish. Publishing does not put it in front
                of clients on its own.
              </span>
            </>
          )}
        </div>

        {onCreateLink && isOnStepTwo && (
          <button
            type="button"
            onClick={() => {
              close();
              onCreateLink();
            }}
            className="flex cursor-pointer items-center justify-between gap-lg border-t border-border-secondary p-lg text-left hover:bg-bg-secondary"
          >
            <span className="txt-small-medium text-text-primary">
              Create workflow link
            </span>
            <Icons.ChevronRight className="size-4 text-text-tertiary" />
          </button>
        )}

        <div className="flex justify-end gap-md border-t border-border-secondary p-lg">
          {isShownLive ? (
            <Button variant="secondary" size="sm" onClick={close}>
              Close
            </Button>
          ) : isOnStepTwo ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={close}
                disabled={isActivating}
              >
                Not now
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  Promise.resolve(onActivate())
                    .then(close)
                    .catch((error) =>
                      console.error('Could not make the version live:', error)
                    );
                }}
                disabled={isActivating || !isActivationKnown}
                isLoading={isActivating}
              >
                Make live
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={close}
                disabled={isPublishing}
              >
                Close
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  onPublish()
                    .then((didPublish) => {
                      // Stays open on success so step two is the next thing the author sees.
                      if (didPublish) setHasJustPublished(true);
                    })
                    .catch((error) => console.error('Publish failed:', error));
                }}
                disabled={isPublishing}
                isLoading={isPublishing}
              >
                Publish
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
